import { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { getItemReferences } from '../../bffApiCalls/getItemReferences';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { assessmentRegions } from '../../learnosity-regions/AssessmentRegions';
import { v4 as uuid } from 'uuid';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';
import { InlineAssessmentWrapper } from '../InlineAssessmentWrapper/InlineAssessmentWrapper';
import { useAuth0 } from '@auth0/auth0-react';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { useComponentTracking } from '../../hooks/useComponentTracking';
import { Button } from 'primereact/button';
import { BadgeCheck, Calculator, Redo } from 'lucide-react';
import { SkillResourceSteps } from '../../models/Timeline';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { PracticeProblemsSessionQuery } from '../../graphQLOperations/__generated__/PracticeProblemsSessionQuery.graphql';
import { getPracticeProblemsSession, upsertPracticeProblemsSession } from '../../graphQLOperations/PracticeProblems';
import { motion } from 'motion/react';
import { PracticeProblemsSessionMutation } from '../../graphQLOperations/__generated__/PracticeProblemsSessionMutation.graphql';
import { clearProgress } from '../../businessLogicService/practiceProblemsHelper';
import { usePracticeProblemUserActionSave } from '../../custom-hooks/usePracticeProblemUserActionSave';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { AssessmentState } from '../../custom-hooks/useLrnSession';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';
import { LearnosityInitializationStatus } from '../../custom-hooks/useLearnosityItemsInitialization';

interface Props {
  skillCode: string;
  skillId?: string;
  setAssessmentInProgress?: Dispatch<SetStateAction<boolean>>;
  setPrintDisabled?: (state: boolean) => void;
  refetchLrnSession?: () => void;
  setCurrentStep?: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  setSkipNewActivityId?: Dispatch<SetStateAction<boolean>>;
  skipSessionQuery?: boolean;
  learnosityPPKey?: number | null;
}

export const PracticeProblems: FC<Props> = ({
  skillCode,
  skillId,
  setPrintDisabled,
  refetchLrnSession,
  setCurrentStep,
  setSkipNewActivityId,
  skipSessionQuery = false,
  learnosityPPKey,
  setAssessmentInProgress,
}) => {
  // Guided-tour preview: session upserts below are backend writes — guarded locally.
  const previewMode = usePreviewMode();
  const userRedux = useAppSelector(selectUser);
  const { logStarted, logResumed, logReset } = usePracticeProblemUserActionSave();
  const [loading, setLoading] = useState(true);
  const [playerStatus, setPlayerStatus] = useState<LearnosityInitializationStatus>('idle');
  const [loadError, setLoadError] = useState(false);
  const [itemReferences, setItemReferences] = useState<string[] | null>(null);
  const [itemReferencesRetryKey, setItemReferencesRetryKey] = useState(0);
  const itemReferencesFailed = useRef(false);
  const [initObj, setInitObj] = useState<LearnosityRequest | null>(null);
  const [showBadge, setShowBadge] = useState<boolean>(false);
  const [showRetryButton, setShowRetryButton] = useState<boolean>(false);
  const handleStatusChange = useCallback((status: LearnosityInitializationStatus) => {
    setPlayerStatus(status);
    if (status !== 'idle') setLoading(false);
    if (status === 'failed') setLoadError(true);
  }, []);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );
  const initController = useRef<AbortController | null>(null);
  const currentInitRef = useRef<{ sessionId: string; state: AssessmentState } | null>(null);
  const initRetriedRef = useRef(false);

  const [commitPracticeProblemSessionId] = useMutation<PracticeProblemsSessionMutation>(upsertPracticeProblemsSession);
  const { learnosityPracticeProblemsSession } = useLazyLoadQuery<PracticeProblemsSessionQuery>(
    getPracticeProblemsSession,
    // A preview has no student session to resume, so previewMode always skips the query.
    { skillId: skillId!, studentId: `StudentNode:${userRedux?.id}`, skip: skipSessionQuery || previewMode },
    { fetchPolicy: 'network-only', fetchKey: `${skillId}-${learnosityPPKey}` },
  );

  // Track component lifecycle for analytics
  useComponentTracking('Practice Problems');

  const triggerMutationToSaveSessionId = (sessionId: string, action: 'started' | 'reset') => {
    commitPracticeProblemSessionId({
      variables: {
        input: {
          input: {
            sessionId: sessionId,
            studentId: `StudentNode:${userRedux?.id ?? ''}`,
            sessionStartedOn: new Date(),
            skillId: skillId ?? '',
            activityId: `Skill_${skillCode}_Practice_Set`,
            isActive: true,
          },
        },
      },
      onCompleted: (_response, error) => {
        if (error && error.length > 0) {
          console.error('Error saving practice problems session ID:', error[0].message);
        }
        if (action === 'started') {
          setTimeout(() => {
            logStarted(skillId ?? '', sessionId);
          }, 3000);
        } else if (action === 'reset') {
          setTimeout(() => {
            logReset(skillId ?? '', sessionId);
          }, 3000);
        }
      },
    });
  };

  const triggerLearnosityInitObject = (sessionId: string, state: AssessmentState) => {
    currentInitRef.current = { sessionId, state };
    initController.current?.abort();
    const controller = new AbortController();
    initController.current = controller;
    setInitObj(null);
    setPlayerStatus('idle');
    setLoading(!itemReferencesFailed.current && itemReferences?.length !== 0);
    setPrintDisabled?.(true);
    setLoadError(itemReferencesFailed.current);

    fetchInitialization(
      {
        state: state,
        activityId: `Skill_${skillCode}_Practice_Set`,
        userId: userRedux?.sub || '',
        sessionId: sessionId,
        renderingType: 'inline',
        regions: assessmentRegions,
        title: 'Practice Problems',
        showDistractorRationale: true,
        responsiveRegions: true,
        warningOnChange: false,
      },
      { signal: controller.signal },
    )
      .then(initObject => {
        if (controller.signal.aborted) return;
        setInitObj(initObject);
      })
      .catch(err => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.log('Error fetching practice problems init object', err);
        setLoading(false);
        setLoadError(true);
      });
  };

  const handleResetButton = () => {
    const newSessionId = uuid();
    setShowBadge(false);
    clearProgress(userRedux?.id ?? '', skillCode);
    // Keep pending or loaded references; only an intentional retry clears a failed read.
    if (itemReferencesFailed.current) {
      itemReferencesFailed.current = false;
      setItemReferencesRetryKey(key => key + 1);
    }
    triggerLearnosityInitObject(newSessionId, 'initial');
    if (!previewMode) {
      triggerMutationToSaveSessionId(newSessionId, 'reset');
    }
  };

  const handleInitError = () => {
    if (initRetriedRef.current || !currentInitRef.current) return;
    initRetriedRef.current = true;
    const { sessionId, state } = currentInitRef.current;
    triggerLearnosityInitObject(sessionId, state);
  };

  const handleTakeChallenge = useBrainBreakNavigation(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSkipNewActivityId?.(false);
    setAssessmentInProgress?.(true);
    setCurrentStep?.(SkillResourceSteps.SKILL_CHALLENGE);
    refetchLrnSession?.();
  }, 'practice-problems');

  useEffect(() => {
    if (itemReferences) {
      setLoading(false);
    }
  }, [itemReferences]);

  useEffect(() => {
    const controller = new AbortController();
    itemReferencesFailed.current = false;
    setItemReferences(null);
    if (skillCode && userRedux?.sub) {
      getItemReferences(`Skill_${skillCode}_Practice_Set`, getAccessTokenSilently, logout, posthog, {
        signal: controller.signal,
      })
        .then(itemRef => {
          if (controller.signal.aborted) return;
          setItemReferences(itemRef);
        })
        .catch(err => {
          if (controller.signal.aborted || isAbortError(err)) return;
          itemReferencesFailed.current = true;
          console.log('Error fetching item references', err);
          setLoading(false);
          setLoadError(true);
        });
    }
    return () => controller.abort();
  }, [skillCode, userRedux?.sub, itemReferencesRetryKey]);

  useEffect(() => {
    setInitObj(null);
    let sessionId: string;
    if (learnosityPracticeProblemsSession?.sessionId) {
      sessionId = learnosityPracticeProblemsSession.sessionId;
      setShowRetryButton(true);
      logResumed(skillId ?? '', sessionId);
    } else {
      sessionId = uuid();
      if (skillId && !previewMode) triggerMutationToSaveSessionId(sessionId, 'started');
    }

    if (skillCode && userRedux?.sub) {
      triggerLearnosityInitObject(sessionId, learnosityPracticeProblemsSession?.sessionId ? 'resume' : 'initial');
    }
    return () => {
      initController.current?.abort();
    };
  }, [learnosityPracticeProblemsSession?.sessionId, skillCode, userRedux?.sub, previewMode]);

  const sdkBusy = Boolean(
    initObj && itemReferences?.length && !loadError && playerStatus !== 'ready' && playerStatus !== 'failed',
  );

  return (
    <>
      <div
        className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start nc_block nc_learnosity !tw-relative !tw-z-[2]"
        id="practiceProblems"
      >
        <div className="tw-w-full tw-flex tw-flex-row tw-justify-between">
          <h1 className="tw-flex tw-items-center tw-w-100 tw-p-1 tw-gap-x-2">
            <Calculator className="tw-mr-2" />
            Practice Problems
            {showBadge && skillId && (
              <motion.span
                initial={{ opacity: 0, scale: 0, rotateY: -90 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{
                  duration: 1,
                  delay: 1,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
                className="tw-border-solid tw-border tw-border-green-400 tw-bg-green-50 tw-rounded-lg tw-gap-x-2 tw-flex tw-items-center tw-px-3 tw-py-1 tw-font-atkinson tw-text-green-600 tw-text-sm"
              >
                <BadgeCheck className="tw-color-green-600 tw-w-[14px] tw-h-[14px]" /> Practice saved
              </motion.span>
            )}
          </h1>
          {showRetryButton && skillId && (
            <Button
              className="tw-font-atkinson tw-gap-x-2"
              icon={<Redo className="tw-color-blue-500" />}
              iconPos="left"
              label="Start over"
              disabled={loading || sdkBusy}
              link
              onClick={() => handleResetButton()}
            />
          )}
        </div>
        <h3 className="tw-font-normal tw-p-1">
          <span className="tw-font-bold">Do at least 3 problems before the Skill Challenge.</span>
          <br></br>They start easy and get harder, so try one at the beginning, one in the middle, and one at the end.
        </h3>
        {itemReferences?.length && initObj && !loadError ? (
          <InlineAssessmentWrapper
            onInitError={handleInitError}
            setPrintDisabled={setPrintDisabled}
            initObj={initObj}
            itemRefs={itemReferences}
            setShowBadge={setShowBadge}
            setShowRetryButton={setShowRetryButton}
            skillCode={skillCode}
            onStatusChange={handleStatusChange}
            refetchInitialization={refetchInitialization}
          />
        ) : loading ? (
          <ThreeDotLoader dark />
        ) : loadError ? (
          <ErrorBoundaryUI embedded />
        ) : (
          <h3 className="tw-self-center tw-p-1">Practice problems for this skill coming soon!</h3>
        )}
      </div>

      {(!loading || itemReferences !== null) &&
        refetchLrnSession &&
        setCurrentStep &&
        setSkipNewActivityId &&
        setAssessmentInProgress && (
          <div className="tw-flex tw-flex-col tw-items-center">
            <span className="tw-font-atkinson tw-text-2xl tw-font-bold">When you are ready</span>
            <span className="tw-font-atkinson tw-text-2xl">take the Skill Challenge</span>
            <Button className="tw-font-atkinson tw-mt-2" label="Take Challenge" onClick={handleTakeChallenge} />
          </div>
        )}
    </>
  );
};
